import * as React from 'react';

import { type VariantProps, cva } from 'class-variance-authority';

import { cn } from '../lib/utils';

const badgeVariants = cva(
  // No padding/height in the base — those belong to `size`. The base previously
  // hardcoded `px-1.5 py-0.5` with no height at all, so every count bubble in
  // the app invented its own dimensions (`h-4 min-w-4 text-[0.6rem]` on the
  // notification bell, `h-2 w-2` for dots, others elsewhere). That is why no
  // two bubbles matched or aligned.
  // `whitespace-nowrap`: a badge is a short label, never a paragraph. Without
  // it a two-word status ("Fully Paid") breaks across two lines the moment its
  // table column is narrow, which both looks broken and makes that one row
  // taller than every other row in the table.
  'focus:ring-ring inline-flex items-center border font-semibold whitespace-nowrap transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-hidden',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground border-transparent',
        secondary: 'bg-secondary text-secondary-foreground border-transparent',
        destructive: 'text-destructive border-destructive',
        outline: 'text-foreground',
        // Status variants read the semantic roles (tokens.css) instead of
        // hardcoded palette steps. The old `border-green-500 text-green-500`
        // had no fill and no dark-mode handling, so a green-500 label sat on
        // whatever surface it landed on — legible on white, muddy on a dark
        // card. The tinted-chip form (subtle fill + tuned foreground + border)
        // is the pattern the 2,005 hardcodes across the app were hand-rolling,
        // and it resolves correctly in both themes.
        success:
          'bg-success-subtle text-success-foreground border-success-border',
        warning:
          'bg-warning-subtle text-warning-foreground border-warning-border',
        danger: 'bg-danger-subtle text-danger-foreground border-danger-border',
        info: 'bg-info-subtle text-info-foreground border-info-border',
        neutral:
          'bg-neutral-role-subtle text-neutral-role-foreground border-neutral-role-border',
      },
      size: {
        default: 'rounded-md px-1.5 py-0.5 text-xs',
        sm: 'rounded px-1 py-0 text-[0.6875rem]',
        // Numeric bubbles (unread counts). Square-ish at one digit and growing
        // into a pill at two+, because min-w equals the height. `tabular-nums`
        // keeps 1 and 9 the same width so a row of badges does not jitter as
        // counts change, and `justify-center` is what actually centres the
        // digit — the base only sets align-items.
        count:
          'h-4 min-w-4 justify-center rounded-full px-1 text-[0.625rem] leading-none tabular-nums',
        // Presence indicator: no glyph, so it needs an explicit square.
        dot: 'h-2 w-2 justify-center rounded-full p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, size, ...props }: BadgeProps) {
  return (
    <div
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
